import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Package, Pencil, Search } from "lucide-react";
import type { MockProductV2 } from "../pages/v2/mockData";
import { fetchUserProducts } from "../pages/v2/productsApi";
import CachedImg from "./CachedImg";
import ProductGridSkeleton from "./ProductGridSkeleton";
import { AddProductFlow } from "./AddProductFlow";
import { EditProductFlow } from "./EditProductFlow";

// Un producto está "afinándose" si se importó/creó hace poco y todavía no tiene
// categoría (`modelView`) — el back se la pone unos segundos después. Acotamos a
// "reciente" (por `createdAt`) para NO marcar productos viejos sin categorizar.
const RECENT_MS = 15 * 60 * 1000;
const isAfinando = (p: MockProductV2): boolean =>
  !p.modelView &&
  !!p.createdAt &&
  Date.now() - new Date(p.createdAt).getTime() < RECENT_MS;
// "Pendiente" para el polling: sin imagen aún, o afinándose.
const isPending = (p: MockProductV2): boolean => !p.imageUrl || isAfinando(p);

/**
 * Tab "Mis productos" de la pantalla Mi marca: barra de búsqueda + botón para
 * agregar producto (mismo flujo/UI que seleccionar productos) + grilla de los
 * productos de la marca activa.
 */
export const MiMarcaProductsTab = ({
  companyId,
  brandId,
}: {
  companyId: string | null;
  brandId: string | null;
}) => {
  const [products, setProducts] = useState<MockProductV2[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  // Producto en edición (abre el panel "Editar producto" — VariantForm).
  const [editingProduct, setEditingProduct] = useState<MockProductV2 | null>(
    null,
  );
  const pollRef = useRef<number | null>(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current !== null) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  // Trae los productos y los setea, SIN tocar el skeleton (para refrescos
  // silenciosos del polling). `fetchUserProducts` maneja ambos casos: con marca
  // activa trae los de esa marca + los sin marca (`brand_id null`); SIN marca
  // activa trae todos los de la empresa. Ver [[productos-huerfanos-brand-null]].
  const fetchAndSet = useCallback(async (): Promise<MockProductV2[]> => {
    if (!companyId) return [];
    const p = await fetchUserProducts(companyId, brandId);
    setProducts(p);
    return p;
  }, [companyId, brandId]);

  // Polling acotado: re-consulta cada 5s mientras haya productos "pendientes"
  // (sin imagen aún, o recién importados sin categorizar → badge "afinando"),
  // y corta cuando ya están todos o al agotar la ventana (~90s). SIN tocar la
  // base — puro re-fetch del front.
  const startPolling = useCallback(() => {
    stopPolling();
    let ticks = 0;
    const MAX_TICKS = 18; // ~90s a 5s
    pollRef.current = window.setInterval(async () => {
      ticks += 1;
      const p = await fetchAndSet();
      if (!p.some(isPending) || ticks >= MAX_TICKS) stopPolling();
    }, 5000);
  }, [fetchAndSet, stopPolling]);

  const load = useCallback(async () => {
    // Sin companyId no hay nada que traer; apagamos el loading para no dejar el
    // skeleton colgado.
    if (!companyId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const p = await fetchAndSet();
      // Si al entrar ya hay pendientes (import en curso), arrancar el polling.
      if (p.some(isPending)) startPolling();
    } finally {
      setLoading(false);
    }
  }, [companyId, fetchAndSet, startPolling]);

  // Tras crear/importar productos: refresco inmediato + polling para que las
  // imágenes y la categoría aparezcan SOLAS (sin refrescar, sin tocar la base).
  const refreshAfterSave = useCallback(() => {
    void fetchAndSet();
    startPolling();
  }, [fetchAndSet, startPolling]);

  useEffect(() => {
    load();
    // Al cambiar de marca o desmontar, cortar cualquier polling en curso.
    return stopPolling;
  }, [load, stopPolling]);

  const q = query.toLowerCase().trim();
  const filtered = q
    ? products.filter((p) => (p.name || "").toLowerCase().includes(q))
    : products;

  return (
    <div>
      {/* Barra de búsqueda + Agregar producto */}
      <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="relative flex-1">
          <label htmlFor="mimarca-search-productos" className="sr-only">
            Buscar productos
          </label>
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <input
            id="mimarca-search-productos"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar productos..."
            className="w-full pl-10 pr-3 h-[var(--btn-h)] rounded-md border border-neutral-200 bg-surface-card text-[length:var(--text-body)] text-neutral-900 placeholder:text-neutral-400 focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none transition-colors"
          />
        </div>
        <AddProductFlow
          companyId={companyId}
          brandId={brandId}
          onSaved={refreshAfterSave}
          className="w-full sm:w-auto"
        />
      </div>

      {loading ? (
        <ProductGridSkeleton
          count={9}
          gridClassName="grid grid-cols-2 xl:grid-cols-3 gap-3"
          tileClassName="rounded-xl"
          withLabel
        />
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center text-center py-16 text-brand-black/40">
          <Package className="w-8 h-8 mb-3" strokeWidth={1.5} />
          <p className="text-[14px]">
            {query
              ? "No se encontraron productos."
              : "Todavía no hay productos en esta marca."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 xl:grid-cols-3 gap-3">
          {filtered.map((p) => (
            <div key={p.id} className="flex flex-col">
              <div className="group relative aspect-square rounded-xl overflow-hidden bg-neutral-100">
                {p.imageUrl ? (
                  <CachedImg
                    src={p.imageUrl}
                    alt={p.name}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-brand-black/25">
                    <Package className="w-6 h-6" strokeWidth={1.5} />
                  </div>
                )}
                {isAfinando(p) && (
                  <span className="absolute top-1.5 left-1.5 z-10 inline-flex items-center gap-1 rounded-full bg-black/55 px-2 py-0.5 text-[11px] font-medium text-white backdrop-blur-sm">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Analizando producto
                  </span>
                )}
                {/* Editar: lápiz en la esquina inferior derecha (hover en
                    desktop, siempre en mobile), igual que en Mis Modelos. */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingProduct(p);
                  }}
                  aria-label="Editar producto"
                  className="absolute bottom-2 right-2 z-10 inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/95 text-brand-black/80 shadow-md ring-1 ring-black/[0.06] backdrop-blur-md transition-all active:scale-90 hover:bg-white opacity-100 sm:opacity-0 sm:group-hover:opacity-100"
                >
                  <Pencil className="w-4 h-4" />
                </button>
              </div>
              <p className="mt-1.5 text-[12.5px] text-brand-black/80 leading-tight line-clamp-2">
                {p.name}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Panel "Editar producto" (VariantForm) — desktop = panel lateral,
          mobile/PWA = bottom-sheet (lo resuelve el propio VariantForm). */}
      {editingProduct && (
        <EditProductFlow
          companyId={companyId}
          product={{ id: editingProduct.id, name: editingProduct.name }}
          onClose={() => setEditingProduct(null)}
          onSaved={fetchAndSet}
        />
      )}
    </div>
  );
};
